import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./auth.css";
import olympeakLogo from "../assets/olympeak_logo_new.png";

const API_BASE =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_API_URL || "http://localhost:8788"|| "http://localhost:3000"
    : "";

const authBg = "/images/olympeak_visual_1.png";

export default function ResetPasswordPage() {
  const navigate = useNavigate();

  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || "";
  }, []);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");

  const passwordsMismatch = pass && pass2 && pass !== pass2;

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    setInfo("");

    if (!token) {
      setErr("missing_token");
      return;
    }

    if (!pass || pass.length < 8) {
      setErr("invalid_password");
      return;
    }

    if (pass !== pass2) {
      setErr("passwords_mismatch");
      return;
    }

    setLoading(true);
    try {
      // We'll implement this route next in auth.ts
      const res = await fetch(`${API_BASE}/api/auth/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: pass }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErr(data.error || "request_failed");
        return;
      }

      setInfo("password_updated");

      // small UX delay so user sees it worked
      setTimeout(() => {
        navigate("/auth?mode=login", { replace: true });
      }, 700);
    } catch (e2) {
      console.error("Reset password confirm error:", e2);
      setErr("request_failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="page-wrapper no-wallpaper auth-bg"
      style={{ backgroundImage: `url(${authBg})` }}
    >
      <div className="page-container auth-container">
        <div className="app-card auth-card glass">
          <div className="auth-brand">
            <a href="/" className="auth-logo-link">
              <img
                src={olympeakLogo}
                alt="OlymPeak"
                className="auth-brand-logo"
              />
            </a>
          </div>

          <div className="auth-form">
            <form onSubmit={handleSubmit} className="form">
              <label className="app-label">New password</label>
              <input
                className="app-input"
                type="password"
                placeholder="At least 8 characters"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                autoComplete="new-password"
                minLength={8}
              />

              <label className="app-label">Repeat new password</label>
              <input
                className="app-input"
                type="password"
                placeholder="Repeat your password"
                value={pass2}
                onChange={(e) => setPass2(e.target.value)}
                autoComplete="new-password"
                minLength={8}
              />

              {passwordsMismatch && (
                <div className="app-error">{prettyErr("passwords_mismatch")}</div>
              )}

              {err && <div className="app-error">{prettyErr(err)}</div>}
              {info && <div className="app-success">{prettyInfo(info)}</div>}

              <button className="btn primary" disabled={loading || passwordsMismatch}>
                {loading ? "Saving…" : "Save new password"}
              </button>

              <div className="hint hint-small">
                <button
                  type="button"
                  className="app-link link-button"
                  onClick={() => navigate("/auth?mode=login")}
                  disabled={loading}
                >
                  Back to login
                </button>
              </div>

              {!token && (
                <div className="hint hint-small" style={{ marginTop: 6 }}>
                  This reset link looks incomplete. Please use the link from your email.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function prettyErr(code) {
  const map = {
    passwords_mismatch: "Passwords do not match.",
    invalid_password: "Password must be at least 8 characters.",
    missing_token: "Reset link is invalid or expired. Please request a new one.",
    request_failed: "Request failed. Try again.",
    internal_error: "Something went wrong. Please try again.",
    invalid_or_expired_token: "Reset link is invalid or expired. Please request a new one.",
  };
  return map[code] || code;
}

function prettyInfo(code) {
  const map = {
    password_updated: "Password updated. Redirecting to login…",
  };
  return map[code] || code;
}